import React, { useEffect, useRef, useState } from 'react';
import {
  getAreaFloorPlan,
  getDevices,
  getFloorPlan,
  getSites,
  getWebSocketUrl,
  updateDevicePosition,
  uploadAreaFloorPlan,
  uploadFloorPlan,
} from '../services/api';
import { Device, Reading, Site } from '../types';
import { useIsMobile } from '../hooks/useIsMobile';

interface MapMetric {
  key: string;
  label: string;
  unit: string;
  type: 'number' | 'string';
}

type DeviceState = 'online' | 'offline' | 'maintenance';

export default function MapPage() {
  const isMobile = useIsMobile(860);
  const [devices, setDevices] = useState<Device[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [tenantFloorPlan, setTenantFloorPlan] = useState<string | null>(null);
  const [floorPlan, setFloorPlan] = useState<string | null>(null);
  const [floorPlanUrl, setFloorPlanUrl] = useState<string | null>(null);
  const [readings, setReadings] = useState<Record<string, Reading>>({});
  const [hoveredDevice, setHoveredDevice] = useState<Device | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  const visibleDevices = selectedAreaId
    ? devices.filter((d) => d.area_id === selectedAreaId)
    : devices;
  const selectedArea = findArea(sites, selectedAreaId);
  const activeDevice =
    (selectedDevice && visibleDevices.find((d) => d.id === selectedDevice.id)) ||
    (hoveredDevice && visibleDevices.find((d) => d.id === hoveredDevice.id)) ||
    null;
  const onlineCount = visibleDevices.filter((d) => getDeviceState(d) === 'online').length;
  const maintenanceCount = visibleDevices.filter((d) => getDeviceState(d) === 'maintenance').length;
  const offlineCount = visibleDevices.filter((d) => getDeviceState(d) === 'offline').length;
  const positionedCount = visibleDevices.filter((d) => d.pos_x != null && d.pos_y != null).length;

  useEffect(() => {
    loadData();
    const interval = setInterval(loadDevices, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadPlan(selectedAreaId);
  }, [selectedAreaId, tenantFloorPlan]);

  useEffect(() => {
    const wsUrl = getWebSocketUrl('/ws');
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'readings') {
          const next: Record<string, Reading> = {};
          for (const reading of msg.data) {
            next[reading.device_id] = reading;
          }
          setReadings(next);
        }
      } catch {
        // Ignore malformed WebSocket payloads.
      }
    };

    return () => ws.close();
  }, []);

  useEffect(() => {
    return () => {
      if (floorPlanUrl) URL.revokeObjectURL(floorPlanUrl);
    };
  }, [floorPlanUrl]);

  function applyPlan(svg: string | null) {
    setFloorPlan(svg);
    setFloorPlanUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      return svg ? URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' })) : null;
    });
  }

  async function loadDevices() {
    try {
      const devs = await getDevices();
      setDevices(devs);
    } catch {
      // Keep the last known map state visible.
    }
  }

  async function loadData() {
    try {
      const [devs, plan, siteList] = await Promise.all([getDevices(), getFloorPlan(), getSites()]);
      setDevices(devs);
      setSites(siteList);
      setTenantFloorPlan(plan.floor_plan || null);
    } catch {
      // Keep the page shell usable even if the backend is temporarily unavailable.
    }
  }

  async function loadPlan(areaId: string) {
    if (areaId) {
      try {
        const res = await getAreaFloorPlan(areaId);
        applyPlan(res.floor_plan || tenantFloorPlan);
        return;
      } catch {
        // Fall through to tenant-level plan.
      }
    }
    applyPlan(tenantFloorPlan);
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const text = await file.text();
      if (selectedAreaId) {
        await uploadAreaFloorPlan(selectedAreaId, text);
      } else {
        await uploadFloorPlan(text);
        setTenantFloorPlan(text);
      }
      applyPlan(text);
    } catch {
      window.alert('Failed to upload floor plan');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  }

  function startDrag(deviceId: string) {
    setHoveredDevice(null);
    setDragging(deviceId);
  }

  function handleMouseDown(deviceId: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    startDrag(deviceId);
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!dragging || !canvasRef.current) return;
    moveDevice(dragging, e.clientX, e.clientY);
  }

  function handleMouseUp() {
    persistDrag();
  }

  function handleTouchStart(deviceId: string, e: React.TouchEvent) {
    e.stopPropagation();
    startDrag(deviceId);
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!dragging || !canvasRef.current) return;
    const touch = e.touches[0];
    moveDevice(dragging, touch.clientX, touch.clientY);
  }

  function handleTouchEnd() {
    persistDrag();
  }

  function moveDevice(deviceId: string, clientX: number, clientY: number) {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    setDevices((prev) =>
      prev.map((device) =>
        device.id === deviceId
          ? {
              ...device,
              pos_x: clamp(x),
              pos_y: clamp(y),
            }
          : device
      )
    );
  }

  function persistDrag() {
    if (!dragging) return;
    const device = devices.find((d) => d.id === dragging);
    if (device && device.pos_x !== undefined && device.pos_y !== undefined) {
      updateDevicePosition(device.id, device.pos_x, device.pos_y).catch(() => {});
    }
    setDragging(null);
  }

  function getMappedMetrics(device: Device): MapMetric[] {
    const mappings = device.parser_config?.mappings;
    const mappedMetrics: MapMetric[] =
      mappings && typeof mappings === 'object'
        ? Object.entries(mappings as Record<string, any>)
            .filter(([key, cfg]) => cfg && key !== 'api_key')
            .map(([key, cfg]) => ({
              key,
              label: cfg.label || formatMetricLabel(key),
              unit: cfg.unit || '',
              type: (cfg.type === 'string' ? 'string' : 'number') as 'number' | 'string',
            }))
        : [];

    const isVirtualOrGateway =
      !!device.mqtt_connection_id ||
      (device.parser_type && device.parser_type !== 'direct') ||
      mappedMetrics.length > 0;

    if (isVirtualOrGateway) {
      return mappedMetrics;
    }

    return [
      { key: 'temperature', label: 'Temperature', unit: 'deg C', type: 'number' },
      { key: 'humidity', label: 'Humidity', unit: '%', type: 'number' },
      { key: 'pressure', label: 'Pressure', unit: 'hPa', type: 'number' },
      { key: 'battery', label: 'Battery', unit: '%', type: 'number' },
      { key: 'signal_strength', label: 'Signal', unit: 'dBm', type: 'number' },
    ];
  }

  function getAllMetrics(device: Device, reading: Reading | undefined): MapMetric[] {
    const metrics = getMappedMetrics(device);
    const mappedKeys = new Set(metrics.map((m) => m.key));
    const meta = reading?.metadata || {};
    Object.entries(meta).forEach(([key, value]) => {
      if (key === 'api_key') return;
      if (value === undefined || value === null) return;
      if (mappedKeys.has(key)) return;
      metrics.push({
        key,
        label: formatMetricLabel(key),
        unit: '',
        type: typeof value === 'string' ? 'string' : 'number',
      });
    });
    return metrics;
  }

  function getMetricValue(reading: Reading | undefined, key: string): { value: any; isString: boolean } {
    if (!reading) return { value: undefined, isString: false };
    if (key === 'temperature') return { value: reading.temperature, isString: false };
    if (key === 'humidity') return { value: reading.humidity, isString: false };
    if (key === 'pressure') return { value: reading.pressure, isString: false };
    if (key === 'battery') return { value: reading.battery, isString: false };
    if (key === 'signal_strength') return { value: reading.signal_strength, isString: false };
    const value = reading.metadata?.[key];
    return { value, isString: typeof value === 'string' };
  }

  return (
    <div className="sd-map-page">
      <section className="sd-page-header sd-map-heading">
        <div className="sd-heading">
          <div className="sd-eyebrow">MONITOR</div>
          <h1>{selectedArea ? selectedArea.name : 'Floor Plan'}</h1>
          <p>
            Drag device nodes to update their stored position. Colors reflect live device state.
          </p>
        </div>
        <div className="sd-page-actions">
          <span className={`sd-sync-chip ${floorPlan ? 'is-synced' : 'is-empty'}`}>
            {floorPlan ? 'Layout synced' : 'No layout'}
          </span>
          <label className="sd-secondary-button sd-upload-action">
            {loading ? 'Uploading...' : floorPlan ? 'Change SVG' : 'Upload SVG'}
            <input type="file" accept=".svg" onChange={handleFileUpload} />
          </label>
        </div>
      </section>

      <section className="sd-map-stat-grid">
        <MapStat label="Visible nodes" value={visibleDevices.length} detail={`${positionedCount} positioned`} tone="blue" />
        <MapStat label="Online" value={onlineCount} detail="Healthy heartbeat" tone="success" />
        <MapStat label="Maintenance" value={maintenanceCount} detail="Suppressed alarms" tone="warning" />
        <MapStat label="Offline" value={offlineCount} detail="Needs review" tone="danger" />
      </section>

      <section className="sd-map-workspace">
        <article className="sd-panel sd-map-canvas-panel">
          <div className="sd-panel-header sd-map-toolbar">
            <div>
              <h2>Site layout</h2>
              <p>{selectedArea ? 'Area-level floor plan' : 'Tenant default floor plan'}</p>
            </div>
            <div className="sd-map-controls">
              {sites.length > 0 && (
                <label>
                  <span>Area</span>
                  <select value={selectedAreaId} onChange={(e) => setSelectedAreaId(e.target.value)}>
                    <option value="">All Areas</option>
                    {sites.map((site) => (
                      <optgroup key={site.id} label={site.name}>
                        {site.areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>

          {!floorPlan && (
            <div className="sd-map-empty">
              <div className="sd-map-empty-grid" aria-hidden="true" />
              <h3>Upload an SVG floor plan</h3>
              <p>
                Seedronics will overlay live devices on your tenant or area layout and save node positions as you drag.
              </p>
              <label className="sd-primary-button sd-upload-action">
                {loading ? 'Uploading...' : 'Upload floor plan'}
                <input type="file" accept=".svg" onChange={handleFileUpload} />
              </label>
            </div>
          )}

          {floorPlan && (
            <>
              <div
                ref={canvasRef}
                className="sd-map-canvas"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {floorPlanUrl && (
                  <img src={floorPlanUrl} alt="Uploaded floor plan" draggable={false} />
                )}

                {visibleDevices.map((device) => {
                  const reading = readings[device.id];
                  const state = getDeviceState(device);
                  const metrics = getAllMetrics(device, reading);
                  return (
                    <div key={device.id}>
                      <button
                        className={`sd-map-node is-${state} ${dragging === device.id ? 'is-dragging' : ''}`}
                        style={{
                          left: `${device.pos_x ?? 50}%`,
                          top: `${device.pos_y ?? 50}%`,
                        }}
                        onMouseDown={(e) => handleMouseDown(device.id, e)}
                        onTouchStart={(e) => handleTouchStart(device.id, e)}
                        onMouseEnter={() => setHoveredDevice(device)}
                        onMouseLeave={() => setHoveredDevice(null)}
                        onClick={() => setSelectedDevice(device)}
                        type="button"
                        aria-label={`Select ${device.name}`}
                      >
                        <span />
                      </button>
                      <div
                        className="sd-map-node-label"
                        style={{
                          left: `${device.pos_x ?? 50}%`,
                          top: `calc(${device.pos_y ?? 50}% + 19px)`,
                        }}
                      >
                        {device.name}
                      </div>
                      {hoveredDevice?.id === device.id && (
                        <DeviceHoverCard
                          device={device}
                          metrics={metrics.slice(0, 5)}
                          reading={reading}
                          getMetricValue={getMetricValue}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="sd-map-legend">
                <LegendDot state="online" label="Online" />
                <LegendDot state="maintenance" label="Maintenance" />
                <LegendDot state="offline" label="Offline" />
              </div>
            </>
          )}
        </article>

        <NodeInspector
          device={activeDevice}
          getAllMetrics={getAllMetrics}
          getMetricValue={getMetricValue}
          reading={activeDevice ? readings[activeDevice.id] : undefined}
          visibleDevices={visibleDevices}
        />
      </section>
    </div>
  );
}

function NodeInspector({
  device,
  getAllMetrics,
  getMetricValue,
  reading,
  visibleDevices,
}: {
  device: Device | null;
  getAllMetrics: (device: Device, reading: Reading | undefined) => MapMetric[];
  getMetricValue: (reading: Reading | undefined, key: string) => { value: any; isString: boolean };
  reading?: Reading;
  visibleDevices: Device[];
}) {
  if (!device) {
    return (
      <aside className="sd-panel sd-node-inspector">
        <div className="sd-panel-header">
          <div>
            <h2>Node inspector</h2>
            <p>Select a device node to inspect live status.</p>
          </div>
        </div>
        <div className="sd-inspector-empty">
          <b>{visibleDevices.length}</b>
          <span>visible devices</span>
          <p>Click any node on the floor plan to view readings, topic details, and saved position.</p>
        </div>
      </aside>
    );
  }

  const state = getDeviceState(device);
  const metrics = getAllMetrics(device, reading);

  return (
    <aside className="sd-panel sd-node-inspector">
      <div className="sd-panel-header">
        <div>
          <h2>Node inspector</h2>
          <p>Live device details</p>
        </div>
      </div>

      <div className="sd-inspector-body">
        <div className="sd-inspector-title">
          <span className={`sd-map-status-dot is-${state}`} />
          <div>
            <h3>{device.name}</h3>
            <p>{device.id.slice(0, 8)}</p>
          </div>
        </div>
        <span className={`sd-status-pill is-${state}`}>
          <span />
          {state === 'maintenance' ? 'Maintenance' : state}
        </span>

        <div className="sd-inspector-reading">
          <strong>{pickPrimaryReading(device, reading, metrics, getMetricValue)}</strong>
          <span>{readableMetricLabel(metrics[0]?.label || 'Latest reading')}</span>
        </div>

        <div className="sd-inspector-facts">
          <FactRow label="Site" value={device.site_name || 'Unassigned'} />
          <FactRow label="Area" value={device.area_name || 'Unassigned'} />
          <FactRow label="Location" value={device.location || 'No location'} />
          <FactRow label="Topic" value={device.topic || 'No topic'} mono />
          <FactRow label="Last seen" value={formatAgo(device.last_reading_at)} />
          <FactRow label="Position" value={`${formatCoordinate(device.pos_x)}, ${formatCoordinate(device.pos_y)}`} mono />
        </div>

        <div>
          <div className="sd-inspector-section-title">Live metrics</div>
          <div className="sd-inspector-metrics">
            {device.latest_battery != null && (
              <MetricTile label="Battery" unit="%" value={device.latest_battery} />
            )}
            {device.latest_signal_strength != null && (
              <MetricTile label="Signal" unit="dBm" value={device.latest_signal_strength} />
            )}
            {metrics.slice(0, 6).map((metric) => {
              const { value } = getMetricValue(reading, metric.key);
              return (
                <MetricTile
                  key={metric.key}
                  label={metric.label}
                  unit={metric.unit}
                  value={value}
                  type={metric.type}
                />
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}

function DeviceHoverCard({
  device,
  getMetricValue,
  metrics,
  reading,
}: {
  device: Device;
  getMetricValue: (reading: Reading | undefined, key: string) => { value: any; isString: boolean };
  metrics: MapMetric[];
  reading?: Reading;
}) {
  const state = getDeviceState(device);

  return (
    <div
      className="sd-map-hover-card"
      style={{
        left: `${device.pos_x ?? 50}%`,
        top: `${device.pos_y ?? 50}%`,
      }}
    >
      <div className="sd-hover-title">
        <strong>{device.name}</strong>
        <span className={`sd-state-chip is-${state === 'offline' ? 'open' : 'resolved'}`}>
          {state}
        </span>
      </div>
      {metrics.map((metric) => {
        const { value } = getMetricValue(reading, metric.key);
        return (
          <div className="sd-hover-row" key={metric.key}>
            <span>{metric.label}</span>
            <b>{formatMetricValue(value, metric.type, metric.unit)}</b>
          </div>
        );
      })}
      {!reading && <p>No live data available</p>}
    </div>
  );
}

function MapStat({
  detail,
  label,
  tone,
  value,
}: {
  detail: string;
  label: string;
  tone: 'blue' | 'danger' | 'success' | 'warning';
  value: number;
}) {
  return (
    <article className={`sd-map-stat is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function LegendDot({ label, state }: { label: string; state: DeviceState }) {
  return (
    <span>
      <i className={`sd-map-status-dot is-${state}`} />
      {label}
    </span>
  );
}

function FactRow({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div className="sd-fact-row">
      <span>{label}</span>
      <strong className={mono ? 'sd-mono' : undefined}>{value}</strong>
    </div>
  );
}

function MetricTile({
  label,
  type = 'number',
  unit,
  value,
}: {
  label: string;
  type?: 'number' | 'string';
  unit: string;
  value?: any;
}) {
  return (
    <div className="sd-metric-tile">
      <span>{readableMetricLabel(label)}</span>
      <strong>{formatMetricValue(value, type, unit)}</strong>
    </div>
  );
}

function findArea(sites: Site[], areaId: string) {
  if (!areaId) return null;
  for (const site of sites) {
    const area = site.areas.find((item) => item.id === areaId);
    if (area) return area;
  }
  return null;
}

function clamp(value: number) {
  return Math.max(0, Math.min(100, value));
}

function getDeviceState(device: Device): DeviceState {
  if (device.maintenance_mode) return 'maintenance';
  return device.status === 'online' ? 'online' : 'offline';
}

function formatMetricLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function readableMetricLabel(label: string) {
  return label.replace(/deg C/g, 'degrees C');
}

function formatMetricValue(value: any, type: 'number' | 'string', unit: string) {
  if (value === undefined || value === null || value === '') return '--';
  if (type === 'number' && typeof value === 'number') {
    return `${value.toFixed(1)}${unit ? ` ${unit}` : ''}`;
  }
  return `${String(value)}${unit ? ` ${unit}` : ''}`;
}

function pickPrimaryReading(
  device: Device,
  reading: Reading | undefined,
  metrics: MapMetric[],
  getMetricValue: (reading: Reading | undefined, key: string) => { value: any; isString: boolean }
) {
  const firstMetric = metrics[0];
  if (firstMetric) {
    const { value } = getMetricValue(reading, firstMetric.key);
    return formatMetricValue(value, firstMetric.type, firstMetric.unit);
  }
  if (device.latest_battery != null) return `${device.latest_battery.toFixed(0)} %`;
  return '--';
}

function formatCoordinate(value?: number) {
  return value == null ? '--' : value.toFixed(1);
}

function formatAgo(value?: string) {
  if (!value) return 'No readings';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return 'Unknown';
  const seconds = Math.max(0, Math.round((Date.now() - time) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
